import React, { useRef, useState, useContext, useEffect } from "react";
import "./App.css";
//import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { unstable_HistoryRouter as Router } from "react-router-dom";
import { useEffectOnce } from "./hooks/useEffectOnce";
import { directionService } from "./services/directionService";
import customhistory from "./history/customhistory";
import LoggerContext from "./context/LoggerProvider";
import { DirectionProvider } from "@radix-ui/react-direction";
import { PresListProvider } from "./context/PresListProvider";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import RoutesWithHeader from "./components/RoutesWithHeader";
import {BrandingProvider, ApplyBrandingCSSVars, BrandingContext} from './branding/BrandingContext';
import { SocketProvider } from "./context/SocketProvider";
import { ChatDrawerProvider } from "./context/ChatDrawerContext";
import { FeedbackProvider } from "./context/FeedbackContext";
import { SpaceProvider } from "./context/SpaceProvider";
import { CompanionProvider } from "./context/CompanionProvider";
import InstallPrompt from "./components/InstallPrompt";
import EnablePushPrompt from "./components/EnablePushPrompt";
import { Analytics } from "@vercel/analytics/react"
import PageTitleUpdater from "./components/PageTitleUpdater";
import NavigationHistoryTracker from "./components/NavigationHistoryTracker";
import ErrorBoundary from "./components/ErrorBoundary";
import ClearCacheButton from "./components/ClearCacheButton";
import { useLanguageDirection } from "./components/Localize";
export const WSContext = React.createContext();

function App() {
  const [roomId, setRoomId] = useState(0);
  const [listRefresh, setListRefresh] = useState(false);
  const [shadcnDirection, setShadcnDirection] = useState(document.body.dir);

  // Ensure document.body.dir is always set based on current language,
  // even on routes where the Header (and its Localize component) is hidden
  useLanguageDirection();

  const [globalAbortController, setGlobalAbortController] = useState(
    new AbortController()
  );

  const { Logger } = useContext(LoggerContext);
  const roomRef = useRef(null);


  useEffectOnce(() => {
    // unset semaphore
    sessionStorage.removeItem("semaphore");

    // Remove legacy brandId from localStorage and URL
    localStorage.removeItem("brandId");

    // Remove "br" parameter from URL if it exists
    const params = new URLSearchParams(window.location.search);
    if (params.has("br")) {
      params.delete("br");
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    }

    // NOTE: Legacy WebSocket chat server connection removed - now using Socket.IO (SocketProvider)
    // All real-time communication (notifications, chat, mentor events) now handled via Socket.IO

    let directionUnsubscribe = directionService
      .getDirectionChange()
      .subscribe(() => {
        setShadcnDirection(document.body.dir);
      });

    return () => {
      Logger.debug("UNLOADING App.js ... normally this should not happen!");
      directionUnsubscribe.unsubscribe();
    };
  }, []);

  useEffect(() => {
    roomRef.current = roomId;
  }, [roomId]);

  // In development mode, skip ErrorBoundary to allow React's error overlay to show
  const AppContent = (
    <div className="text-black h-screen w-full flex flex-col">
      <Analytics/>
      <WSContext.Provider
        value={{
          socket: null, // Legacy WebSocket removed - use Socket.IO via SocketProvider instead
          roomId,
          setRoomId,
          roomRef,
          listRefresh,
          setListRefresh,
          globalAbortController,
          setGlobalAbortController,
        }}
      >
        <QueryClientProvider client={queryClient}>
          <BrandingProvider>
            <ApplyBrandingCSSVars />
            <DirectionProvider dir={shadcnDirection}>
            <PresListProvider>
              <SocketProvider>
                <SpaceProvider>
                  <ChatDrawerProvider>
                    <FeedbackProvider>
                          <Router history={customhistory}>
                              <CompanionProvider>
                                <PageTitleUpdater />
                                <NavigationHistoryTracker />
                                <RoutesWithHeader />
                              </CompanionProvider>
                          </Router>
                          <InstallPrompt />
                          <EnablePushPrompt />
                          {/*<ClearCacheButton />*/}
                    </FeedbackProvider>
                  </ChatDrawerProvider>
                </SpaceProvider>
              </SocketProvider>
            </PresListProvider>
          </DirectionProvider>
          </BrandingProvider>
        </QueryClientProvider>
      </WSContext.Provider>
    </div>
  );

  // Only use ErrorBoundary in production
  return process.env.NODE_ENV === 'production'
    ? <ErrorBoundary>{AppContent}</ErrorBoundary>
    : AppContent;
}

export default App;
